// src/components/Box/Box.tsx
import { forwardRef } from "react";
import { jsx } from "@emotion/react";
import { css as transformStyleObject } from "@theme-ui/css";
import merge from "deepmerge";

// src/components/Box/selector.ts
var createteSelector = (selectors, states, suffix = "") => {
  return selectors.map(
    (selector2) => states.map((state2) => `${selector2}${state2}${suffix ? ` ${suffix}` : ""}`)
  ).flat().join(", ");
};
var selector = {
  self: "&",
  grouped: ["[role=group]", "[data-group]"]
};
var state = {
  none: [""],
  hover: [":hover:not([disabled])", "[data-hover]"],
  focus: [":focus", "[data-focus]"],
  focusVisible: [":focus-visible", "[data-focus-visible]"],
  active: [":active", "[data-active]"],
  disabled: ["[disabled]", "[aria-disabled=true]", "[data-disabled]"],
  readOnly: ["[readonly]", "[aria-readonly=true]", "[data-read-only]"],
  checked: ["[aria-checked=true]", "[data-checked]"],
  indeterminate: ["[aria-checked=mixed]", "[data-indeterminate]"],
  selected: ["[aria-selected=true]", "[data-selected]"],
  error: [":invalid", "[aria-invalid=true]", "[data-error]"],
  expanded: ["[aria-expanded=true]", "[data-expanded]"],
  required: [":required", "[aria-required]"]
};
var pseudos = {
  "&:hover": createteSelector([selector.self], state.hover),
  "&:focus": createteSelector([selector.self], state.focus),
  "&:focus-visible": createteSelector([selector.self], state.focusVisible),
  "&:active": createteSelector([selector.self], state.active),
  "&:disabled": createteSelector([selector.self], state.disabled),
  "&:read-only": createteSelector([selector.self], state.readOnly),
  "&:checked": createteSelector([selector.self], state.checked),
  "&:selected": createteSelector([selector.self], state.selected),
  "&:indeterminate": createteSelector([selector.self], state.indeterminate),
  "&:error": createteSelector([selector.self], state.error),
  "&:expanded": createteSelector([selector.self], state.expanded),
  "&:required": createteSelector([selector.self], state.required),
  "&:in-group": createteSelector(selector.grouped, state.none, selector.self),
  "&:hover-group": createteSelector(
    selector.grouped,
    state.hover,
    selector.self
  ),
  "&:focus-group": createteSelector(
    selector.grouped,
    state.focus,
    selector.self
  ),
  "&:active-group": createteSelector(
    selector.grouped,
    state.active,
    selector.self
  ),
  "&:error-group": createteSelector(
    selector.grouped,
    state.error,
    selector.self
  )
};
var transformPseudos = (styles) => {
  let result = {};
  for (let key in styles) {
    const value = styles[key];
    if (key in pseudos) {
      key = pseudos[key];
    }
    if (typeof value === "object") {
      result[key] = transformPseudos(value);
      continue;
    }
    result[key] = value;
  }
  return result;
};

// src/components/Box/Box.tsx
var createThemedStyle = ({ __baseCSS, styles, css }) => (theme) => {
  const themedStyles = merge.all([
    transformStyleObject(__baseCSS)(theme),
    transformStyleObject(styles)(theme),
    ...Array.isArray(css) ? css.map((c) => transformStyleObject(c)(theme)) : [transformStyleObject(css)(theme)]
  ]);
  return transformPseudos(themedStyles);
};
var Box = forwardRef(
  ({
    as = "div",
    children,
    __baseCSS,
    css,
    display,
    height,
    width,
    minWidth,
    maxWidth,
    position,
    top,
    bottom,
    right,
    left,
    zIndex,
    p,
    px,
    py,
    pt,
    pb,
    pl,
    pr,
    m,
    mx,
    my,
    mt,
    mb,
    ml,
    mr,
    flexDirection,
    flexWrap,
    flexShrink,
    flexGrow,
    alignItems,
    justifyContent,
    bg,
    border,
    borderRadius,
    boxShadow,
    opacity,
    overflow,
    transition,
    ...props
  }, ref) => jsx(
    as,
    {
      ...props,
      css: createThemedStyle({
        __baseCSS,
        css,
        styles: {
          display,
          height,
          width,
          minWidth,
          maxWidth,
          position,
          top,
          bottom,
          right,
          left,
          zIndex,
          p,
          px,
          py,
          pt,
          pb,
          pl,
          pr,
          m,
          mx,
          my,
          mt,
          mb,
          ml,
          mr,
          flexDirection,
          flexWrap,
          flexShrink,
          flexGrow,
          alignItems,
          justifyContent,
          bg,
          border,
          borderRadius,
          boxShadow,
          opacity,
          overflow,
          transition
        }
      }),
      ref
    },
    children
  )
);

// src/components/Global/Global.tsx
import React2 from "react";
import { Global as EmotionGlobal } from "@emotion/react";

// src/hooks/useTheme.tsx
import React, {
  createContext,
  useCallback,
  useContext
} from "react";
import {
  css as transformStyleObject2,
  get as getfromTheme
} from "@theme-ui/css";
import { ThemeProvider as EmotionProvider } from "@emotion/react";
var __defaultTheme = {};
var InternalContext = createContext(__defaultTheme);
var useTheme = () => {
  const theme = useContext(InternalContext);
  const css = useCallback(
    (style) => transformStyleObject2(style)(theme),
    [theme]
  );
  const get2 = useCallback((path) => getfromTheme(theme, path), [theme]);
  return { theme, css, get: get2 };
};
function ThemeProvider({
  theme,
  children
}) {
  return /* @__PURE__ */ React.createElement(EmotionProvider, {
    theme
  }, /* @__PURE__ */ React.createElement(InternalContext.Provider, {
    value: theme
  }, children));
}

// src/components/Global/normalize.ts
var document = {
  "html, body": {
    height: "100%"
  },
  html: {
    textSizeAdjust: "none"
  },
  body: {
    lineHeight: 1.5,
    WebkitFontSmoothing: "antialiased",
    margin: 0
  },
  "@media screen and (prefers-reduced-motion: reduce), (update: slow)": {
    "*": {
      animationDuration: "0.001ms !important",
      animationIterationCount: "1 !important",
      transitionDuration: "0.001ms !important"
    }
  }
};
var element = {
  "*, *::before, *::after": {
    boxSizing: "border-box"
  },
  "*": {
    margin: 0
  },
  a: {
    textDecoration: "none"
  },
  "p, h1, h2, h3, h4, h5, h6": {
    overflowWrap: "break-word"
  },
  "img, picture, video, canvas, svg": {
    display: "block",
    maxWidth: "100%"
  },
  svg: {
    display: "block",
    fill: "currentColor"
  },
  button: {
    display: "block",
    appearance: "none",
    font: "inherit",
    background: "transparent",
    textAlign: "center"
  },
  input: {
    display: "block",
    appearance: "none",
    font: "inherit",
    "&::-ms-clear": {
      display: "none"
    },
    "&::-webkit-search-cancel-button": {
      WebkitAppearance: "none"
    }
  },
  select: {
    display: "block",
    appearance: "none",
    font: "inherit",
    "&::-ms-expand": {
      display: "none"
    }
  },
  textarea: {
    display: "block",
    appearance: "none",
    font: "inherit"
  }
};

// src/components/Global/Global.tsx
var Global = ({ normalizeDocument = true, selector: selector2 }) => {
  const { css, theme } = useTheme();
  const rootStyles = css((theme == null ? void 0 : theme.root) || {});
  const styles = [
    normalizeDocument ? document : {},
    selector2 ? { [`:where(${selector2})`]: element } : element,
    selector2 ? { [`:where(${selector2})`]: rootStyles } : rootStyles
  ];
  return /* @__PURE__ */ React2.createElement(EmotionGlobal, {
    styles
  });
};

// src/components/SVG/SVG.tsx
import { forwardRef as forwardRef2 } from "react";
import { jsx as jsx2 } from "@emotion/react";

// src/hooks/useComponentStyles.ts
import merge2 from "deepmerge";
import { useRef } from "react";
import isEqual from "react-fast-compare";
var get = (obj, path, fallback) => {
  const key = path.split(".");
  let result = obj;
  for (let i = 0, length = key.length; i < length; i++) {
    if (!result)
      break;
    result = result[key[i]];
  }
  return result === void 0 ? fallback : result;
};
function useComponentStyles(componentName, props = {}, options = {}) {
  var _a, _b;
  const { theme } = useTheme();
  const componentStyles = get(theme, `components.${componentName}`);
  const stylesRef = useRef({});
  if (componentStyles) {
    const base = componentStyles.base || {};
    const size = ((_a = componentStyles.size) == null ? void 0 : _a[props.size]) || {};
    const variant = ((_b = componentStyles.variant) == null ? void 0 : _b[props.variant]) || {};
    let styles = merge2.all([base, size, variant]);
    if (options.parts) {
      styles = options.parts.reduce((result, part) => {
        result[part] = styles[part] || {};
        return result;
      }, {});
    }
    if (!isEqual(stylesRef.current, styles)) {
      stylesRef.current = styles;
    }
  }
  return stylesRef.current;
}

// src/hooks/useResponsiveValue.ts
import { useEffect, useState } from "react";
var emptyBreakpoints = ["40em", "52em", "64em"];
var useResponsiveValue = (values, defaultIndex = 0) => {
  const { theme } = useTheme();
  const breakpoints = theme.breakpoints || emptyBreakpoints;
  if (defaultIndex < 0 || defaultIndex >= breakpoints.length + 1) {
    throw new RangeError(
      `Default breakpoint index is out of bounds. Theme has ${breakpoints.length + 1} breakpoints, default is ${defaultIndex}.`
    );
  }
  const [index, setIndex] = useState(defaultIndex);
  useEffect(() => {
    const getIndex = () => breakpoints.filter(
      (breakpoint) => window.matchMedia(`screen and (min-width: ${breakpoint})`).matches
    ).length;
    const handleResize = () => {
      const newIndex = getIndex();
      if (index !== newIndex) {
        setIndex(newIndex);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoints, index]);
  return values[index >= values.length ? values.length - 1 : index];
};

// src/hooks/useStateProps.ts
import { useRef as useRef2 } from "react";
import isEqual2 from "react-fast-compare";
var KEBAB_REGEX = /[A-Z\u00C0-\u00D6\u00D8-\u00DE]/g;
var toKebap = (val) => val.replace(KEBAB_REGEX, (match) => `-${match.toLocaleLowerCase()}`);
var useStateProps = (states) => {
  const statePropsRef = useRef2({});
  let stateProps = {};
  for (let state2 in states) {
    if (states[state2]) {
      const key = `data-${toKebap(state2)}`;
      stateProps[key] = "";
    }
  }
  if (!isEqual2(statePropsRef.current, stateProps)) {
    statePropsRef.current = stateProps;
  }
  return statePropsRef.current;
};

// src/components/SVG/SVG.tsx
var toDimension = (value) => Array.isArray(value) ? value.map(ensureNumberOrToken) : ensureNumberOrToken(value);
var ensureNumberOrToken = (value) => typeof value === "string" && /^[0-9]+$/.test(value) ? Number(value) : value;
var SVG = forwardRef2(
  ({ size = 24, fill, children, css: styles, ...props }, ref) => {
    const { css } = useTheme();
    return jsx2(
      "svg",
      {
        ...props,
        css: css({
          ...styles,
          fill,
          width: toDimension(props.width || size),
          height: toDimension(props.height || size)
        }),
        ref
      },
      children
    );
  }
);
export {
  Box,
  Global,
  SVG,
  ThemeProvider,
  __defaultTheme,
  useComponentStyles,
  useResponsiveValue,
  useStateProps,
  useTheme
};
